import { useLogout } from "@shared/hooks/useLogout";
import ReloadPageImage from "@shared/svg/reload-page.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import Tooltip from "@shared/ui/Tooltip";
import LogRocket from "logrocket";
import mixpanel from "mixpanel-browser";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useIntercom } from "react-use-intercom";
import { useChatStore } from "~/stores/chatStore";
import { useGlobalErrorStore } from "~/stores/globalErrorStore";
import { useAppConfigStore } from "~/stores/appConfigStore";
import ConfirmationModal from "~/components/_confirmationModal";
import ImagePreview from "~/components/_imagePreview";
import useAuth from "~/components/general/_use-auth";
import { useSyncBoxBaseUrls } from "~/hooks";
import SplashScreen from "~/components/general/SplashScreen";
import Navigation from "~/features/Navigation/Navigation";
import { useGlobalListener } from "~/hooks/useGlobalListener";
import Main from "~/layout/_main";
import Routes from "~/layout/_routes";
import WorkspaceRedirector from "~/layout/WorkspaceRedirector";
import { useGetIntercomHash } from "~/services/user/_query";
import { isLogRocketEnabled } from "~/utils/_logRocket";
import {
  isMixpanelEnabled,
  registerMixpanelSuperProperties,
} from "~/utils/_mixpanel";

interface RouteConfig {
  path: string;
  exact?: boolean;
  hideNavigation?: boolean;
  [key: string]: any;
}

interface Portal {
  linkedConnection?: string;
  [key: string]: any;
}

interface LayoutProps {
  intl?: any;
  [key: string]: any;
}

let userIdentified: boolean;

const Layout = (props: LayoutProps) => {
  const { messages } = useIntl();
  const location = useLocation();
  const { state } = location as { state?: { welcome?: boolean } };
  const { user = {}, loading } = useAuth() || {};
  const { hash: intercomHash } = useGetIntercomHash({ skip: !user?._id });
  // Publishes the OEM's Box hosts for the explorer and the service layer
  useSyncBoxBaseUrls(user?.oem?.boxProxyEnabled);
  const [routes, setRoutes] = useState<RouteConfig[]>([]);
  const [defaultRoute, setDefaultRoute] = useState<RouteConfig | null>(null);
  const { isErrorAlertOpen } = useGlobalErrorStore();
  const { update: updateIntercom } = useIntercom();
  const appConfig = useAppConfigStore();
  const { logout } = useLogout();

  const imagePreviewOptions = useChatStore((s) => s.imagePreview);
  const setImagePreview = useChatStore((s) => s.setImagePreview);

  const closeImagePreview = () => {
    setImagePreview({ isOpen: false });
  };

  const handleNotification = useCallback(
    (notification: {
      id: string;
      message: string;
      type: "success" | "error" | "info";
      autoClose?: number | false;
      hideProgressBar?: boolean;
    }) => {
      const toastType =
        notification.type === "error" ? TOAST_TYPES.ERROR : TOAST_TYPES.SUCCESS;

      const toastComponent = (
        <Toast type={toastType} message={notification.message} />
      );

      const toastOptions = {
        autoClose: notification.autoClose,
        hideProgressBar: notification.hideProgressBar,
        closeButton: notification.autoClose !== false,
      };

      if (notification.id) {
        toast.update(notification.id, {
          render: toastComponent,
          ...toastOptions,
        });
      } else {
        toast(toastComponent, toastOptions);
      }
    },
    [],
  );

  useGlobalListener({ onNotification: handleNotification });

  useEffect(() => {
    if (isLogRocketEnabled()) {
      if (user?._id && !userIdentified && user?.oem?.usageTracking?.logRocket) {
        LogRocket.init(import.meta.env.VITE_APP_LOGOROCKET_ID, {
          release: `v${import.meta.env.PACKAGE_VERSION}`,
        });
        LogRocket.identify(user?._id, {
          name: user?.name,
          email: user?.email,
          role: user?.role,
        });
        userIdentified = true;
      }
    }
  }, [user]);

  useEffect(() => {
    if (!isMixpanelEnabled() || !user?._id) return;
    mixpanel.identify(user?._id);
    mixpanel.people.set({
      $name: user?.name,
      $email: user?.email,
      role: user?.role,
      oem: user?.oem?.slug,
      oemName: user?.oem?.name,
    });
    // Register super properties for all events
    registerMixpanelSuperProperties(user);
  }, [user]);

  useEffect(() => {
    if (
      !routes.length &&
      Object.keys(user || {}).length &&
      appConfig?.features?.length
    ) {
      let defaultRoute: RouteConfig | null = null;
      const routes = Routes(user, appConfig.features, appConfig);
      setRoutes(routes);

      const hasOem = !!user?.oem?._id;
      const portals = (user?.portals as Portal[]) || [];
      const hasPortals = portals.length > 0;

      if (hasOem) {
        defaultRoute =
          routes.find(
            (route) =>
              route.path === "/app/assets" ||
              route.path === "/app/asset-hub/my-assets",
          ) || null;
      } else if (hasPortals) {
        const firstConnectionId = portals[0]?.linkedConnection;
        if (firstConnectionId) {
          defaultRoute = { path: `/app/workspace/${firstConnectionId}/` };
        }
      } else {
        // No OEM and no portals - user has no workspace access
        toast(
          <Toast
            type={TOAST_TYPES.ERROR}
            message={messages?.common?.errors?.noWorkspaceAccess}
          />,
        );
        logout();
        return;
      }

      setDefaultRoute(defaultRoute);
    }
  }, [user, loading, appConfig?.features]);

  useEffect(() => {
    if (import.meta.env.VITE_APP_INTERCOM_APP_ID && intercomHash) {
      updateIntercom({
        customLauncherSelector: ".help-link",
        hideDefaultLauncher: true,
        userHash: intercomHash,
        name: user?.name,
        email: user?.email,
        company: {
          companyId: user?.oem?.slug,
          name: user?.oem?.name,
        },
      });
    }
  }, [user, intercomHash]);

  // Show welcome toast after successful login (only if user has workspace access)
  useEffect(() => {
    if (state?.welcome && routes.length && defaultRoute) {
      toast(
        <Toast message={(messages as Record<string, any>)?.login?.welcome} />,
        {
          closeButton: false,
        },
      );
      // Clear state without triggering re-render
      window.history.replaceState({}, "");
    }
  }, [routes.length, defaultRoute]);

  if (!routes.length || !defaultRoute) return <SplashScreen />;

  // Check if current route has hideNavigation flag
  const currentRoute = routes.find(
    (route) =>
      route.path === location.pathname ||
      (route.path.includes(":") &&
        new RegExp(
          "^" +
            route.path.replace(/:[^/]+/g, "[^/]+") +
            (route.exact ? "$" : ""),
        ).test(location.pathname)),
  );
  const shouldHideNavigation = currentRoute?.hideNavigation === true;

  return (
    <div className={`flex new-navigation hide-navigation`}>
      <WorkspaceRedirector />
      {!shouldHideNavigation && <Navigation />}
      <div className="navigation-s-overlay" />
      <Main
        routes={routes}
        DefaultRoute={defaultRoute}
        user={user}
        appConfig={appConfig}
        {...props}
      />
      <ConfirmationModal></ConfirmationModal>
      <ImagePreview
        intl={props.intl}
        closeModal={closeImagePreview}
        openedImage={imagePreviewOptions?.openedImage}
        isModalOpen={imagePreviewOptions?.isOpen}
      />

      {isErrorAlertOpen && (
        <AlertBox
          isOpen={isErrorAlertOpen}
          portalSelector=".error-alert-box-modal-portal"
          overlay
          image={<ReloadPageImage width="130" height="130" />}
          title={messages?.common?.reloadAppMessage}
          acceptButtonText={messages?.common?.refresh}
          onAccept={() => {
            window.location.reload();
          }}
        />
      )}
      <Tooltip
        id="navigation-hover-tooltip"
        className="!left-14"
        place="right"
      />
    </div>
  );
};

export { Layout };
export default Layout;
